import * as FileSaver from "file-saver";
import writeExcelFile from "write-excel-file/browser";
import Api from "../../service/api";
import CONFIG from "../../config";

const AUTO_PROCESSED_TAB = "AUTO PROCESSED";
const TABS = [AUTO_PROCESSED_TAB, "EPH", "VENDOR", "NOT FOUND", "NO ACTION"];

const VENDOR_SEARCH_COUNTRY_PREFIXES = [
  "#IQVIA_US",
  "#IQVIA",
  "#Veeva Network",
  "#OPEN-DATA",
];

const firstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const normalizeStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase();

const normalizeSource = (source) =>
  String(source || "")
    .trim()
    .toLowerCase();

const isReviewedStatus = (status) => {
  const normalized = normalizeStatus(status);
  return normalized === "reviewed" || normalized === "reviewd";
};

const isTruthy = (value) => {
  if (typeof value === "boolean") {
    return value;
  }
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return normalized === "true" || normalized === "1" || normalized === "yes";
};

const toNullableBoolean = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  return isTruthy(value);
};

const getAllDCRLinks = (result) => {
  let baseAppUrl = CONFIG.MSAL_REDIRECT_URI;
  const env = process.env.REACT_APP_STAGE;
  if (env === "dev_2") {
    baseAppUrl = "https://ecs-dev.lilly.com";
  }

  const dcrSources = [
    ...(Array.isArray(result?.dcr_ids) ? result.dcr_ids : []),
    ...(Array.isArray(result?.results) ? result.results : []),
  ];

  if (dcrSources.length > 0) {
    return dcrSources
      .map((dcr) => {
        let link = null;
        switch (dcr?.vendor) {
          case "IQVIA_US":
          case "IQVIA":
            link = `${baseAppUrl}/track-iqviaDetail/${dcr?.change_request_id}`;
            break;
          case "VNDR-VN":
            link = `${baseAppUrl}/track-vnDetail/${dcr?.change_request_id}`;
            break;
          case "VNDR-VOD":
            link = `${baseAppUrl}/view-all/requests/${dcr?.change_request_id}/OPEN-DATA`;
            break;
          case "Lilly":
          case "EPH":
            link = `${baseAppUrl}/view-all/requests/${dcr?.change_request_id}/Lilly`;
            break;
          default:
            link = null;
        }

        return {
          id: dcr?.change_request_id,
          vendor: dcr?.vendor,
          link,
        };
      })
      .filter((dcr) => dcr.link !== null && dcr.id)
      .filter(
        (dcr, index, arr) =>
          arr.findIndex(
            (item) =>
              String(item.id || "") === String(dcr.id || "") &&
              String(item.vendor || "") === String(dcr.vendor || ""),
          ) === index,
      );
  }

  return [];
};

const parseDcrOutcomeResult = (payload, shellProfilePayload) => {
  const primaryPayload = payload && typeof payload === "object" ? payload : {};
  const fallbackPayload =
    shellProfilePayload && typeof shellProfilePayload === "object"
      ? shellProfilePayload
      : {};

  const entityType = firstValue(
    primaryPayload?.EntityType,
    primaryPayload?.entityType,
    primaryPayload?.entity_type,
    fallbackPayload?.EntityType,
    fallbackPayload?.entityType,
    fallbackPayload?.entity_type,
    "",
  );

  const hcpEnterpriseId = firstValue(
    primaryPayload?.HcpEnterpriseId,
    primaryPayload?.HCPEnterpriseId,
    primaryPayload?.hcpEnterpriseId,
    primaryPayload?.hcp_enterprise_id,
    fallbackPayload?.HcpEnterpriseId,
    fallbackPayload?.HCPEnterpriseId,
    fallbackPayload?.hcpEnterpriseId,
    fallbackPayload?.hcp_enterprise_id,
    "",
  );

  const hcpEntityId = firstValue(
    primaryPayload?.HcpEntityId,
    primaryPayload?.HCPEntityId,
    primaryPayload?.hcpEntityId,
    primaryPayload?.hcp_entity_id,
    fallbackPayload?.HcpEntityId,
    fallbackPayload?.HCPEntityId,
    fallbackPayload?.hcpEntityId,
    fallbackPayload?.hcp_entity_id,
    "",
  );

  const hcoEnterpriseId = firstValue(
    primaryPayload?.HcoEnterpriseId,
    primaryPayload?.HCOEnterpriseId,
    primaryPayload?.hcoEnterpriseId,
    primaryPayload?.hco_enterprise_id,
    fallbackPayload?.HcoEnterpriseId,
    fallbackPayload?.HCOEnterpriseId,
    fallbackPayload?.hcoEnterpriseId,
    fallbackPayload?.hco_enterprise_id,
    "",
  );

  const hcoEntityId = firstValue(
    primaryPayload?.HcoEntityId,
    primaryPayload?.HCOEntityId,
    primaryPayload?.hcoEntityId,
    primaryPayload?.hco_entity_id,
    fallbackPayload?.HcoEntityId,
    fallbackPayload?.HCOEntityId,
    fallbackPayload?.hcoEntityId,
    fallbackPayload?.hco_entity_id,
    "",
  );

  const processedStatus = firstValue(
    primaryPayload?.processed_status,
    primaryPayload?.processedStatus,
    fallbackPayload?.processed_status,
    fallbackPayload?.processedStatus,
    "",
  );

  const statusRaw = firstValue(
    primaryPayload?.Status,
    primaryPayload?.status,
    fallbackPayload?.Status,
    fallbackPayload?.status,
    undefined,
  );
  const status =
    statusRaw === undefined || statusRaw === null || statusRaw === ""
      ? null
      : isTruthy(statusRaw);

  const hasAnyOutcomeValue =
    !!entityType ||
    !!hcpEnterpriseId ||
    !!hcpEntityId ||
    !!hcoEnterpriseId ||
    !!hcoEntityId ||
    !!processedStatus ||
    status !== null;

  if (!hasAnyOutcomeValue) {
    return null;
  }

  return {
    entityType,
    hcpEnterpriseId,
    hcpEntityId,
    hcoEnterpriseId,
    hcoEntityId,
    processedStatus,
    status,
  };
};

const parseDcrResult = (
  dcrResult,
  fallbackResultId,
  hasDcrResultKey = true,
  rootRow = {},
) => {
  const payload = dcrResult && typeof dcrResult === "object" ? dcrResult : null;
  const rowPayload = rootRow && typeof rootRow === "object" ? rootRow : {};
  const shellProfilePayload =
    rowPayload?.shell_profile_result &&
    typeof rowPayload.shell_profile_result === "object"
      ? rowPayload.shell_profile_result
      : rowPayload?.shellProfileResult &&
          typeof rowPayload.shellProfileResult === "object"
        ? rowPayload.shellProfileResult
        : payload?.shell_profile_result &&
            typeof payload.shell_profile_result === "object"
          ? payload.shell_profile_result
          : payload?.shellProfileResult &&
              typeof payload.shellProfileResult === "object"
            ? payload.shellProfileResult
            : null;
  const hasShellProfile =
    isTruthy(
      firstValue(
        rowPayload?.has_shell_profile,
        rowPayload?.hasShellProfile,
        payload?.has_shell_profile,
        payload?.hasShellProfile,
        false,
      ),
    ) || !!shellProfilePayload;
  const dcrOutcomeResult = parseDcrOutcomeResult(payload, shellProfilePayload);

  const dcrLinks = getAllDCRLinks(payload || {});
  const dcrLinkById = new Map(
    dcrLinks.map((item) => [String(item.id || ""), item.link]),
  );

  const dcrIdsList = Array.isArray(payload?.dcr_ids) ? payload.dcr_ids : [];
  const resultList = Array.isArray(payload?.results) ? payload.results : [];

  const dcrIdItems = dcrIdsList
    .map((item) => ({
      id: firstValue(
        item?.change_request_id,
        item?.changeRequestId,
        item?.id,
        "",
      ),
      vendor: firstValue(item?.vendor, item?.source, ""),
      status: item?.status,
    }))
    .filter((item) => item.id)
    .map((item) => ({
      id: String(item.id),
      vendor: item.vendor ? String(item.vendor) : "-",
      status:
        item.status === undefined || item.status === null || item.status === ""
          ? null
          : isTruthy(item.status),
      link: dcrLinkById.get(String(item.id)) || null,
      sourceType: "dcr_ids",
    }));

  const resultItems = resultList
    .map((item) => ({
      id: firstValue(
        item?.change_request_id,
        item?.changeRequestId,
        item?.id,
        "",
      ),
      vendor: firstValue(item?.vendor, item?.source, ""),
    }))
    .filter((item) => item.id)
    .map((item) => ({
      id: String(item.id),
      vendor: item.vendor ? String(item.vendor) : "-",
      link: dcrLinkById.get(String(item.id)) || null,
      sourceType: "results",
    }));

  const fallbackItems = (fallbackResultId ? [String(fallbackResultId)] : [])
    .filter(Boolean)
    .map((id) => ({
      id,
      vendor: "-",
      link: null,
      sourceType: "fallback",
    }));

  const dcrItems =
    hasDcrResultKey && (dcrIdItems.length > 0 || resultItems.length > 0)
      ? [...dcrIdItems, ...resultItems]
      : hasDcrResultKey
        ? fallbackItems
        : [];
  const hasMeaningfulDcrData =
    dcrItems.length > 0 ||
    !!dcrOutcomeResult ||
    !!firstValue(
      payload?.error,
      payload?.error_message,
      payload?.errorMessage,
      payload?.processed_status,
      payload?.processedStatus,
      payload?.validation_status,
      payload?.validationStatus,
      false,
    );

  const dcrIdsFromResults = dcrItems.map((item) => item.id).filter(Boolean);

  const hasErrorKey = payload?.error !== undefined && payload?.error !== null;
  const processedStatusValue = firstValue(
    payload?.processed_status,
    payload?.processedStatus,
    "",
  );
  const isProcessedStatusFailed =
    String(processedStatusValue).trim().toLowerCase() === "failed";
  const hasError = hasErrorKey
    ? isTruthy(payload?.error)
    : isProcessedStatusFailed;

  return {
    isMissing: !hasDcrResultKey || !hasMeaningfulDcrData,
    hasError,
    errorMessage: firstValue(
      payload?.error_message,
      payload?.errorMessage,
      !hasErrorKey && isProcessedStatusFailed
        ? "Failed to process the DCR."
        : "",
    ),
    errorType: firstValue(payload?.error_type, payload?.errorType, ""),
    processedStatus: processedStatusValue,
    validationStatus: firstValue(
      payload?.validation_status,
      payload?.validationStatus,
      "",
    ),
    hasShellProfile,
    shellProfileResult: shellProfilePayload
      ? parseDcrOutcomeResult({}, shellProfilePayload)
      : null,
    dcrOutcomeResult,
    dcrItems,
    dcrIdItems,
    resultItems,
    dcrIds: dcrIdsFromResults,
  };
};

const parseSubscriptionResult = (row) => {
  const shellProfileResult =
    row?.shell_profile_result && typeof row.shell_profile_result === "object"
      ? row.shell_profile_result
      : row?.shellProfileResult && typeof row.shellProfileResult === "object"
        ? row.shellProfileResult
        : {};

  const shellProfileStatusRaw = firstValue(
    row?.shell_profile_status,
    row?.shellProfileStatus,
    shellProfileResult?.shell_profile_status,
    shellProfileResult?.shellProfileStatus,
    shellProfileResult?.Status,
    shellProfileResult?.status,
    undefined,
  );

  const shellProfileStatus =
    shellProfileStatusRaw === undefined ||
    shellProfileStatusRaw === null ||
    shellProfileStatusRaw === ""
      ? null
      : isTruthy(shellProfileStatusRaw);

  const hasKey = (key) =>
    Object.prototype.hasOwnProperty.call(row || {}, key) ||
    Object.prototype.hasOwnProperty.call(shellProfileResult || {}, key);

  const hasAnyKey = (keys = []) => keys.some((key) => hasKey(key));

  const isEnabledByKey = (key) => {
    if (!hasKey(key)) {
      return false;
    }
    const value = firstValue(row?.[key], shellProfileResult?.[key], false);
    return isTruthy(value);
  };

  const isEnabledByKeys = (keys = []) => {
    if (!hasAnyKey(keys)) {
      return false;
    }
    const value = firstValue(
      ...keys.map((key) =>
        firstValue(row?.[key], shellProfileResult?.[key], undefined),
      ),
      false,
    );
    return isTruthy(value);
  };

  const normalizeErrors = () => {
    const errorValue = firstValue(
      shellProfileResult?.errors,
      shellProfileResult?.error,
      shellProfileResult?.error_message,
      shellProfileResult?.errorMessage,
      "",
    );

    if (Array.isArray(errorValue)) {
      return errorValue.filter(Boolean).map((item) => String(item));
    }
    if (errorValue && typeof errorValue === "object") {
      return Object.entries(errorValue).map(
        ([key, value]) => `${key}: ${String(value)}`,
      );
    }
    if (errorValue) {
      return [String(errorValue)];
    }
    return [];
  };

  const hcpSource = firstValue(
    row?.hcp_selected_source,
    row?.hcpSource,
    row?.hcp_source,
    row?.source,
    "",
  );
  const hcoSource = firstValue(
    row?.hco_selected_source,
    row?.hcoSource,
    row?.hco_source,
    row?.source,
    "",
  );
  const parentHcoSource = firstValue(
    row?.parent_hco_selected_source,
    row?.parentHcoSource,
    row?.parent_hco_source,
    row?.hco_selected_source,
    row?.hcoSource,
    row?.hco_source,
    row?.source,
    "",
  );

  const hcoData = {
    enterpriseId: firstValue(
      shellProfileResult?.HcoEnterpriseId,
      shellProfileResult?.HCOEnterpriseId,
      shellProfileResult?.hcoEnterpriseId,
      shellProfileResult?.hco_enterprise_id,
      "",
    ),
    entityId: firstValue(
      shellProfileResult?.HcoEntityId,
      shellProfileResult?.HCOEntityId,
      shellProfileResult?.hcoEntityId,
      shellProfileResult?.hco_entity_id,
      "",
    ),
    foundInEph: firstValue(
      shellProfileResult?.HcoOneKeyIdFoundInEPH,
      shellProfileResult?.HCOOneKeyIdFoundInEPH,
      shellProfileResult?.hcoOneKeyIdFoundInEPH,
      shellProfileResult?.hco_one_key_id_found_in_eph,
      false,
    ),
  };

  const hcpData = {
    enterpriseId: firstValue(
      shellProfileResult?.HcpEnterpriseId,
      shellProfileResult?.HCPEnterpriseId,
      shellProfileResult?.hcpEnterpriseId,
      shellProfileResult?.hcp_enterprise_id,
      "",
    ),
    entityId: firstValue(
      shellProfileResult?.HcpEntityId,
      shellProfileResult?.HCPEntityId,
      shellProfileResult?.hcpEntityId,
      shellProfileResult?.hcp_entity_id,
      "",
    ),
    foundInEph: firstValue(
      shellProfileResult?.HcpOneKeyIdFoundInEPH,
      shellProfileResult?.HCPOneKeyIdFoundInEPH,
      shellProfileResult?.hcpOneKeyIdFoundInEPH,
      shellProfileResult?.hcp_one_key_id_found_in_eph,
      false,
    ),
  };

  const parentHcoData = {
    enterpriseId: firstValue(
      shellProfileResult?.ParentHcoEnterpriseId,
      shellProfileResult?.ParentHCOEnterpriseId,
      shellProfileResult?.parentHcoEnterpriseId,
      shellProfileResult?.parent_hco_enterprise_id,
      "",
    ),
    entityId: firstValue(
      shellProfileResult?.ParentHcoEntityId,
      shellProfileResult?.ParentHCOEntityId,
      shellProfileResult?.parentHcoEntityId,
      shellProfileResult?.parent_hco_entity_id,
      "",
    ),
    foundInEph: firstValue(
      shellProfileResult?.ParentHcoOneKeyIdFoundInEPH,
      shellProfileResult?.ParentHCOOneKeyIdFoundInEPH,
      shellProfileResult?.parentHcoOneKeyIdFoundInEPH,
      shellProfileResult?.parent_hco_one_key_id_found_in_eph,
      false,
    ),
  };

  const entityDetails = [];

  if (
    isEnabledByKeys([
      "hcp_subscription_status",
      "hcpSubscriptionStatus",
      "HcpSubscriptionStatus",
      "HCPSubscriptionStatus",
    ])
  ) {
    entityDetails.push({
      key: "hcp",
      label: "HCP",
      source: hcpSource,
      ...hcpData,
    });
  }

  if (
    isEnabledByKeys([
      "hco_subscription_status",
      "hcoSubscriptionStatus",
      "HcoSubscriptionStatus",
      "HCOSubscriptionStatus",
    ])
  ) {
    entityDetails.push({
      key: "hco",
      label: "HCO",
      source: hcoSource,
      ...hcoData,
    });
  }

  if (
    isEnabledByKeys([
      "parent_hco_subscription_status",
      "parentHcoSubscriptionStatus",
      "ParentHcoSubscriptionStatus",
      "ParentHCOSubscriptionStatus",
    ])
  ) {
    entityDetails.push({
      key: "parent_hco",
      label: "Parent HCO",
      source: parentHcoSource,
      ...parentHcoData,
    });
  }

  const hasMeaningfulSubscriptionData =
    shellProfileStatus !== null ||
    entityDetails.length > 0 ||
    normalizeErrors().length > 0;

  return {
    isMissing: !hasMeaningfulSubscriptionData,
    shellProfileStatus,
    shellProfileStatusRaw,
    errors: normalizeErrors(),
    entityDetails,
    updatedAt: firstValue(
      row?.shell_profile_updated_at,
      row?.shellProfileUpdatedAt,
      shellProfileResult?.shell_profile_updated_at,
      shellProfileResult?.shellProfileUpdatedAt,
      "",
    ),
  };
};

const parseConsentResult = (row) => {
  const consentPayload =
    row?.consent_result && typeof row.consent_result === "object"
      ? row.consent_result
      : row?.consentResult && typeof row.consentResult === "object"
        ? row.consentResult
        : {};

  const consentUploadedRaw = firstValue(
    consentPayload?.consent_uploaded,
    consentPayload?.consentUploaded,
    undefined,
  );
  const hasConsentResultKey =
    Object.prototype.hasOwnProperty.call(row || {}, "consent_result") ||
    Object.prototype.hasOwnProperty.call(row || {}, "consentResult");
  const consentUploaded = toNullableBoolean(consentUploadedRaw);
  const consentUploadedAt = firstValue(
    consentPayload?.consent_uploaded_at,
    consentPayload?.consentUploadedAt,
    "",
  );
  const emailConsentStatus = toNullableBoolean(
    firstValue(
      consentPayload?.email_consent_status,
      consentPayload?.emailConsentStatus,
      undefined,
    ),
  );
  const phoneConsentStatus = toNullableBoolean(
    firstValue(
      consentPayload?.phone_consent_status,
      consentPayload?.phoneConsentStatus,
      undefined,
    ),
  );
  const emailConsentError = String(
    firstValue(
      consentPayload?.email_consent_error,
      consentPayload?.emailConsentError,
      "",
    ) || "",
  ).trim();
  const phoneConsentError = String(
    firstValue(
      consentPayload?.phone_consent_error,
      consentPayload?.phoneConsentError,
      "",
    ) || "",
  ).trim();

  const hasConsentUploadDetails =
    consentUploaded !== null || Boolean(consentUploadedAt);
  const hasConsentStatusDetails =
    emailConsentStatus !== null || phoneConsentStatus !== null;
  const hasConsentErrorDetails =
    Boolean(emailConsentError) || Boolean(phoneConsentError);
  const hasMeaningfulConsentData =
    hasConsentUploadDetails ||
    hasConsentStatusDetails ||
    hasConsentErrorDetails;

  return {
    isMissing: !hasConsentResultKey || !hasMeaningfulConsentData,
    consentUploaded,
    consentUploadedAt,
    emailConsentStatus,
    phoneConsentStatus,
    emailConsentError,
    phoneConsentError,
  };
};

const getResponseRows = (responseData) => {
  if (Array.isArray(responseData)) {
    return responseData;
  }
  if (Array.isArray(responseData?.records)) {
    return responseData.records;
  }
  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }
  if (Array.isArray(responseData?.results)) {
    return responseData.results;
  }
  if (Array.isArray(responseData?.items)) {
    return responseData.items;
  }
  return [];
};

const getSelectionIdBySource = (selectedEntity, source) => {
  if (!selectedEntity || typeof selectedEntity !== "object") {
    return "";
  }

  const normalizedSource = normalizeSource(source);
  if (normalizedSource === "eph") {
    const inferredType = String(
      firstValue(
        selectedEntity?.type,
        selectedEntity?.entityType,
        selectedEntity?.individual?.type,
        "",
      ),
    )
      .trim()
      .toUpperCase();
    const isHcp =
      inferredType === "HCP" ||
      (selectedEntity.HCPUniqueId && !selectedEntity.HCOUniqueId);
    const isHco =
      inferredType === "HCO" ||
      inferredType === "WORKPLACE" ||
      (selectedEntity.HCOUniqueId && !selectedEntity.HCPUniqueId);

    if (isHcp) {
      return (
        firstValue(
          selectedEntity.HCPUniqueId,
          selectedEntity.HCPReferenceId,
          selectedEntity.EntityId,
          selectedEntity.HCOUniqueId,
          selectedEntity.HCOReferenceId,
        ) || ""
      );
    }

    if (isHco) {
      return (
        firstValue(
          selectedEntity.HCOUniqueId,
          selectedEntity.HCOReferenceId,
          selectedEntity.EntityId,
          selectedEntity.HCPUniqueId,
          selectedEntity.HCPReferenceId,
        ) || ""
      );
    }

    return (
      firstValue(
        selectedEntity.HCPUniqueId,
        selectedEntity.HCOUniqueId,
        selectedEntity.EntityId,
        selectedEntity.HCPReferenceId,
        selectedEntity.HCOReferenceId,
      ) || ""
    );
  }

  return (
    firstValue(
      selectedEntity?.entityId,
      selectedEntity?.entity?.vid__v,
      selectedEntity?.entity?.veevaid__v,
      selectedEntity?.individual?.individualEid,
      selectedEntity.onekeyEid,
      selectedEntity.onekeyid,
      selectedEntity?.activity?.activityEid,
      selectedEntity.HCPReferenceId,
      selectedEntity.HCOReferenceId,
      selectedEntity.EntityId,
    ) || ""
  );
};

const formatInputFieldLabel = (fieldKey = "") => {
  const titleCase = String(fieldKey)
    .replace(/_/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

  return titleCase
    .replace(/\bId\b/g, "ID")
    .replace(/\bHco\b/g, "HCO")
    .replace(/\bHcp\b/g, "HCP")
    .replace(/\bRel\b/g, "REL")
    .replace(/\bCda\b/g, "CDA");
};

const buildInputEntityFields = (entityType, entity) => {
  if (!entity || typeof entity !== "object") {
    return [];
  }

  const fields = [
    {
      label: entityType === "HCP" ? "Enterprise ID" : "Enterprise ID",
      value: firstValue(
        entity.enterprise_id,
        entity.EntityId,
        entity.entity_id,
        entity.HCPUniqueId,
        entity.HCOUniqueId,
        entity.HCPReferenceId,
        entity.HCOReferenceId,
        entity.onekeyEid,
        entity.onekeyid,
      ),
    },
    {
      label: "First Name",
      value: firstValue(entity.first_name, entity.firstName),
    },
    {
      label: "Last Name",
      value: firstValue(entity.last_name, entity.lastName),
    },
    {
      label: "Middle Name",
      value: firstValue(entity.middle_name, entity.middleName),
    },
    {
      label: "Name",
      value: firstValue(entity.name, entity.hco_name),
    },
    {
      label: "Identifier Type",
      value: firstValue(entity.identifier_type, entity.identifier_1_type),
    },
    {
      label: "Identifier ID",
      value: firstValue(entity.identifier_id, entity.identifier_1_id),
    },
    {
      label: "Country",
      value: firstValue(
        entity.country_code,
        entity.hco_adrs_country,
      ),
    },
    {
      label: "Address",
      value: firstValue(entity.hcp_adrs_line_1, entity.hco_adrs_line_1),
    },
    {
      label: "City",
      value: firstValue(entity.hcp_adrs_city, entity.hco_adrs_city),
    },
    {
      label: "Zip",
      value: firstValue(entity.hcp_adrs_zip, entity.hco_adrs_zip),
    },
    { label: "Email", value: entity.email },
  ];

  const normalizedEntityType = String(entityType || "")
    .trim()
    .toUpperCase();

  // For parent HCO inputs, include every rel_* key from payload so no relation field is missed.
  const relFields =
    normalizedEntityType === "PARENT_HCO"
      ? Object.entries(entity)
          .filter(
            ([key, value]) =>
              /^rel_/i.test(String(key || "")) &&
              value !== undefined &&
              value !== null &&
              value !== "",
          )
          .map(([key, value]) => ({
            label: formatInputFieldLabel(key),
            value,
          }))
      : [];

  return [...fields, ...relFields].filter(
    (f) => f.value !== undefined && f.value !== null && f.value !== "",
  );
};

const mapSelectionLabel = (entity, fallbackLabel) => {
  if (fallbackLabel === "Parent HCO") {
    return "Parent HCO";
  }

  const type = String(entity?.type || entity?.entityType || "").toUpperCase();
  if (type === "HCP") {
    return "HCP";
  }
  if (type === "HCO") {
    return "HCO";
  }
  if (type === "WORKPLACE") {
    return fallbackLabel;
  }
  return fallbackLabel;
};

const formatListValue = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(", ");
  }
  return value;
};

const mapSourceLabel = (source) => {
  const normalized = normalizeSource(source);
  if (normalized === "vendor") {
    return "IQVIA";
  }
  if (normalized === "eph") {
    return "EPH";
  }
  if (normalized === "vod") {
    return "VOD";
  }
  return normalized ? normalized.toUpperCase() : "-";
};

const getAddressParts = (entity) => {
  const openDataEntity = entity?.entity;
  const openDataPrimaryAddress =
    Array.isArray(openDataEntity?.addresses__v) &&
    openDataEntity.addresses__v.length > 0
      ? openDataEntity.addresses__v.find(
          (address) =>
            String(address?.primary_cda__v || "").toUpperCase() === "Y",
        ) ||
        openDataEntity.addresses__v.find(
          (address) => Number(address?.address_ordinal__v) === 1,
        ) ||
        openDataEntity.addresses__v[0]
      : null;
  const profile = entity?.individual || entity?.hcp || entity?.HCP || entity;
  const workplace =
    entity?.workplace || entity?.parent_hco || entity?.parentHco || {};
  const primaryAddress = workplace?.workplaceAddresses?.["P,1"]?.address || {};
  const postal = primaryAddress?.postalTownReference || {};

  const line1 = firstValue(
    openDataPrimaryAddress?.formatted_address__v,
    openDataPrimaryAddress?.address_line_1__v,
    openDataPrimaryAddress?.street_address_1__v,
    openDataEntity?.street_address_1__v,
    entity?.address_line_1,
    entity?.addressLine1,
    entity?.hcp_adrs_line_1,
    entity?.hco_adrs_line_1,
    profile?.address_line_1,
    profile?.addressLine1,
    primaryAddress?.addressLine1,
    primaryAddress?.addressShortLabel,
    primaryAddress?.addressLongLabel,
    entity?.Address,
    entity?.address,
  );
  const line2 = firstValue(
    openDataPrimaryAddress?.address_line_2__v,
    entity?.address_line_2,
    entity?.addressLine2,
    entity?.hcp_adrs_line_2,
    entity?.hco_adrs_line_2,
    profile?.address_line_2,
    profile?.addressLine2,
    primaryAddress?.addressLine2,
  );
  const city = firstValue(
    openDataPrimaryAddress?.city_cda__v,
    openDataEntity?.city_cda__v,
    entity?.city,
    entity?.hcp_adrs_city,
    entity?.hco_adrs_city,
    postal?.villageLabel,
  );
  const state = firstValue(
    openDataPrimaryAddress?.state_cda__v,
    openDataEntity?.state_cda__v,
    entity?.state,
    entity?.state_province,
    entity?.hcp_adrs_state,
    entity?.hco_adrs_state,
    postal?.regionLabel,
  );
  const zip = firstValue(
    openDataPrimaryAddress?.postal_code__v,
    openDataPrimaryAddress?.postal_code_cda__v,
    openDataEntity?.postal_code_cda__v,
    entity?.zip,
    entity?.zip_code,
    entity?.postal_code,
    entity?.hcp_adrs_zip,
    entity?.hco_adrs_zip,
    primaryAddress?.zipCode,
  );
  const country = firstValue(
    openDataPrimaryAddress?.country__v,
    openDataEntity?.primary_country__v,
    openDataEntity?.country_cda__v,
    entity?.country,
    entity?.country_code,
    entity?.CountryCode,
    entity?.hcp_adrs_country,
    entity?.hco_adrs_country,
    postal?.countryLabel,
  );

  return {
    line1,
    line2,
    city,
    state,
    zip,
    country,
  };
};

const getPrimarySpecialty = (entity) =>
  firstValue(
    entity?.entity?.specialty_1__v,
    entity?.entity?.spec_1_local__v,
    entity?.primary_specialty,
    entity?.primarySpecialty,
    entity?.["individual.speciality1"],
    entity?.speciality1,
    entity?.specialty1,
    entity?.PrimarySpecialty,
    entity?.speciality,
    entity?.specialty,
    entity?.Specialities?.[0]?.value,
    entity?.Specialities?.[0],
    entity?.individual?.primary_specialty,
    entity?.individual?.primarySpecialty,
    entity?.individual?.speciality1,
    entity?.individual?.specialty1,
    entity?.individual?.Specialities?.[0]?.value,
    entity?.individual?.Specialities?.[0],
  );

const getSelectionIdLabelBySource = (selectedEntity, source) => {
  const normalizedSource = normalizeSource(source);
  if (normalizedSource === "eph") {
    return "Enterprise ID";
  }

  const hasOpenDataEntity =
    !!selectedEntity?.entity && typeof selectedEntity.entity === "object";
  return hasOpenDataEntity ? "Entity ID" : "OneKey ID";
};

const buildSelectionFields = (entity, source, fallbackLabel) => {
  if (!entity || typeof entity !== "object") {
    return [];
  }

  const normalizedSource = normalizeSource(source);
  const isEph = normalizedSource === "eph";
  const idLabel = getSelectionIdLabelBySource(entity, source);
  const vendorEntity =
    entity?.entity && typeof entity.entity === "object" ? entity.entity : null;

  const profile =
    entity?.individual || entity?.hcp || entity?.HCP || vendorEntity || entity;
  const rawType = String(
    firstValue(
      entity?.type,
      entity?.entityType,
      vendorEntity?.entityType,
      profile?.type,
      fallbackLabel,
    ) || "",
  )
    .trim()
    .toUpperCase();
  const isHcoLike =
    rawType === "HCO" ||
    rawType === "WORKPLACE" ||
    String(fallbackLabel || "")
      .toUpperCase()
      .includes("HCO");

  const identityId = isEph
    ? firstValue(
        entity.HCPUniqueId,
        entity.HCOUniqueId,
        entity.HCPReferenceId,
        entity.HCOReferenceId,
      )
    : firstValue(
        entity?.entityId,
        vendorEntity?.vid__v,
        vendorEntity?.veevaid__v,
        entity?.individual?.individualEid,
        entity.onekeyEid,
        entity.onekeyid,
        entity?.activity?.activityEid,
        entity.HCPReferenceId,
        entity.HCOReferenceId,
        entity.EntityId,
      );

  const displayName = firstValue(
    vendorEntity?.full_name__v,
    vendorEntity?.formatted_name__v,
    vendorEntity?.corporate_name__v,
    vendorEntity?.hco_name_cda__v,
    entity.Name,
    entity.name,
    profile?.Name,
    profile?.name,
    entity.hco_name,
  );

  const firstName = firstValue(
    vendorEntity?.first_name__v,
    profile?.FirstName,
    profile?.firstName,
    entity.FirstName,
    entity.first_name,
    entity.firstName,
    entity?.individual?.firstName,
  );
  const lastName = firstValue(
    vendorEntity?.last_name__v,
    profile?.LastName,
    profile?.lastName,
    entity.LastName,
    entity.last_name,
    entity.lastName,
    entity?.individual?.lastName,
  );
  const primarySpecialty = formatListValue(getPrimarySpecialty(entity));
  const address = getAddressParts(entity);
  const classificationCode = firstValue(
    vendorEntity?.hcp_type__v,
    vendorEntity?.hco_type__v,
    entity?.ClassificationCode,
    entity?.classification_code,
    entity?.classification,
    profile?.ClassificationCode,
    profile?.classification_code,
    profile?.classification,
    entity?.activity?.classificationCode,
    entity?.activity?.classification_code,
  );
  const activeFlag = firstValue(
    vendorEntity?.status_cda__v,
    vendorEntity?.hcp_status__v,
    vendorEntity?.hco_status__v,
    entity?.ActiveFlag,
    entity?.active_flag,
    entity?.activeFlag,
    profile?.ActiveFlag,
    profile?.active_flag,
    entity?.activity?.activeFlag,
    entity?.activity?.statusLabel,
    entity?.statusLabel,
  );
  const entityIdValue = firstValue(
    entity?.entityId,
    vendorEntity?.vid__v,
    vendorEntity?.veevaid__v,
    entity?.EntityId,
    entity?.entity_id,
    profile?.EntityId,
    profile?.entity_id,
    entity?.individual?.EntityId,
    entity?.individual?.entity_id,
  );

  const metadataHoverDetails = [
    { label: "Classification Code", value: classificationCode },
    { label: "Active Flag", value: activeFlag },
    { label: "EntityId", value: entityIdValue },
  ].filter(
    (field) =>
      field.value !== undefined && field.value !== null && field.value !== "",
  );

  const sharedAddressDetails = [
    { label: "Address Line 2", value: address.line2 },
    { label: "City", value: address.city },
    { label: "State", value: address.state },
    { label: "Zip", value: address.zip },
    { label: "Country", value: address.country },
  ].filter(
    (field) =>
      field.value !== undefined && field.value !== null && field.value !== "",
  );

  if (isHcoLike) {
    return [
      { label: idLabel, value: identityId, details: metadataHoverDetails },
      { label: "Name", value: displayName },
      { label: "Address", value: address.line1, details: sharedAddressDetails },
    ].filter(
      (field) =>
        field.value !== undefined && field.value !== null && field.value !== "",
    );
  }

  return [
    { label: idLabel, value: identityId, details: metadataHoverDetails },
    { label: "First Name", value: firstName },
    { label: "Last Name", value: lastName },
    { label: "Speciality", value: primarySpecialty },
    { label: "Address", value: address.line1, details: sharedAddressDetails },
  ].filter(
    (field) =>
      field.value !== undefined && field.value !== null && field.value !== "",
  );
};

const mapBulkConsentRow = (row, index) => {
  const input = row?.input || row?.input_record || {};
  const inputRowNumber = firstValue(
    row?.input_record?.input_row_number,
    row?.input?.input_row_number,
    input?.input_row_number,
  );
  const inputEntityType = String(
    firstValue(input?.entity_type, row?.entity_type, ""),
  )
    .trim()
    .toUpperCase();

  let hcpInput = input?.hcp || input?.HCP || null;
  let hcoInput = input?.hco || input?.HCO || null;
  let parentHcoInput = input?.parent_hco || input?.Parent_HCO || null;

  if (!hcpInput && !hcoInput && input && typeof input === "object") {
    if (inputEntityType === "HCP") {
      hcpInput = input;
    } else if (inputEntityType === "HCO") {
      hcoInput = input;
    }
  }

  if (!parentHcoInput) {
    const relEntries = Object.entries(input || {}).filter(
      ([key, value]) =>
        /^rel_/i.test(String(key || "")) &&
        value !== undefined &&
        value !== null &&
        value !== "",
    );

    if (relEntries.length > 0) {
      const relPayload = relEntries.reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});

      parentHcoInput = {
        ...relPayload,
        enterprise_id: firstValue(
          input?.rel_hco_enterprise_id,
          relPayload?.rel_hco_enterprise_id,
        ),
        name: firstValue(input?.rel_hco_name, relPayload?.rel_hco_name),
        country_code: firstValue(
          input?.rel_hco_country_code,
          relPayload?.rel_hco_country_code,
        ),
      };
    }
  }

  const selectedResult = row?.selected_result || row?.selectedResult || {};

  const selectedHcp = selectedResult?.hcp || null;
  const selectedHco = selectedResult?.hco || null;
  const selectedParentHco = selectedResult?.parent_hco || null;

  const primarySelectedEntity = selectedHcp || selectedHco || null;
  const primarySelectedSource = selectedHcp
    ? row?.hcp_selected_source
    : row?.hco_selected_source;
  const parentSelectedSource =
    row?.parent_hco_selected_source ||
    row?.hco_selected_source ||
    row?.hcp_selected_source;

  const status = firstValue(row?.status, row?.Status, "");
  const reviewed = isReviewedStatus(status);
  const flaggedNotFound = isTruthy(row?.is_flagged_as_not_found);
  const source = normalizeSource(
    firstValue(
      row?.source,
      row?.Source,
      row?.hco_source,
      row?.hcp_source,
      row?.parent_hco_source,
      "",
    ),
  );

  let category = "NO ACTION";
  if (!reviewed) {
    category = "NO ACTION";
  } else if (source === "not_found") {
    category = "NOT FOUND";
  } else if (source === "eph") {
    category = "EPH";
  } else if (source === "vendor") {
    category = "VENDOR";
  } else if (reviewed && flaggedNotFound) {
    category = "NOT FOUND";
  }

  const inputHcpFields = buildInputEntityFields("HCP", hcpInput);
  const inputHcoFields = buildInputEntityFields("HCO", hcoInput);
  const inputParentHcoFields = buildInputEntityFields(
    "PARENT_HCO",
    parentHcoInput,
  );

  const selectionPrimaryId = getSelectionIdBySource(
    primarySelectedEntity,
    primarySelectedSource,
  );
  const selectionParentId = getSelectionIdBySource(
    selectedParentHco,
    parentSelectedSource,
  );

  const selectionPrimaryType = getSelectionIdLabelBySource(
    primarySelectedEntity,
    primarySelectedSource,
  );
  const selectionParentType = getSelectionIdLabelBySource(
    selectedParentHco,
    parentSelectedSource,
  );

  const selectionPrimaryFields = buildSelectionFields(
    primarySelectedEntity,
    primarySelectedSource,
    selectedHcp ? "HCP" : "HCO",
  );

  const selectionParentFields = buildSelectionFields(
    selectedParentHco,
    parentSelectedSource,
    "Parent HCO",
  );

  const rawResultId = firstValue(
    row?.resultId,
    row?.result_id,
    row?.dcr_id,
    "",
  );
  const hasDcrResultKey =
    Object.prototype.hasOwnProperty.call(row || {}, "dcr_result") ||
    Object.prototype.hasOwnProperty.call(row || {}, "dce_result") ||
    Object.prototype.hasOwnProperty.call(row || {}, "dcrResult") ||
    Object.prototype.hasOwnProperty.call(row || {}, "dceResult");
  const dcrResult = parseDcrResult(
    firstValue(
      row?.dcr_result,
      row?.dce_result,
      row?.dcrResult,
      row?.dceResult,
      null,
    ),
    rawResultId,
    hasDcrResultKey,
    row,
  );
  const subscriptionResult = parseSubscriptionResult(row || {});
  const consentResult = parseConsentResult(row || {});

  return {
    id: firstValue(row?.pk, row?.id, `consent-${index + 1}`),
    csvRow: firstValue(inputRowNumber, row?.csv_row, row?.csvRow, index + 1),
    category,
    isAutoProcessedStatus:
      normalizeStatus(status).replace(/[\s-]+/g, "_") === "auto_processed",
    inputHcpFields,
    inputHcoFields,
    inputParentHcoFields,
    selectionPrimary: selectionPrimaryId
      ? {
          label: mapSelectionLabel(
            primarySelectedEntity,
            selectedHcp ? "HCP" : "HCO",
          ),
          idType: selectionPrimaryType,
          source: primarySelectedSource,
          id: selectionPrimaryId,
          fields: selectionPrimaryFields,
        }
      : null,
    selectionParent: selectionParentId
      ? {
          label: mapSelectionLabel(selectedParentHco, "Parent HCO"),
          idType: selectionParentType,
          source: parentSelectedSource,
          id: selectionParentId,
          fields: selectionParentFields,
        }
      : null,
    isHcpSelected: toNullableBoolean(
      firstValue(row?.is_hcp_selected, row?.isHcpSelected),
    ),
    isHcoSelected: toNullableBoolean(
      firstValue(row?.is_hco_selected, row?.isHcoSelected),
    ),
    isParentHcoSelected: toNullableBoolean(
      firstValue(row?.is_parent_hco_selected, row?.isParentHcoSelected),
    ),
    consentStatus: firstValue(
      row?.consentStatus,
      row?.consent_status,
      row?.consent_status_label,
      row?.is_consent_applicable !== undefined
        ? isTruthy(row?.is_consent_applicable)
          ? "Applicable"
          : "Not Applicable"
        : undefined,
      row?.status,
      "Applicable",
    ),
    isConsentApplicable: isTruthy(
      firstValue(row?.is_consent_applicable, row?.isConsentApplicable, false),
    ),
    isDcrApplicable: isTruthy(
      firstValue(row?.is_dcr_applicable, row?.isDcrApplicable, false),
    ),
    isSubscriptionApplicable: isTruthy(
      firstValue(
        row?.is_subscription_applicable,
        row?.isSubscriptionApplicable,
        false,
      ),
    ),
    result: firstValue(
      row?.result,
      row?.result_label,
      row?.resultText,
      row?.batch_status,
      "",
    ),
    resultId: firstValue(rawResultId, ""),
    dcrResult,
    subscriptionResult,
    consentResult,
    updatedDate: firstValue(
      row?.updatedDate,
      row?.updated_at,
      row?.updated_date,
      row?.update_date,
      row?.result_updated_date,
      "",
    ),
    recordProcessStatus: String(
      firstValue(row?.record_process_status, row?.recordProcessStatus, ""),
    )
      .trim()
      .toUpperCase(),
  };
};


const joinFieldValues = (fields = []) =>
  fields.map((field) => `${field.label}: ${field.value}`).join(" | ");

export const getSupportedVendorCountries = async () => {
  try {
    const response = await Api.getSelectedDCRCountry();
    const allCountries = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response)
        ? response
        : [];
    const supported = new Set();
    allCountries.forEach((country) => {
      if (!country) return;
      const countryName = String(
        country.country_name || country.name || country || "",
      ).trim();
      const matchedPrefix = VENDOR_SEARCH_COUNTRY_PREFIXES.find((prefix) =>
        countryName.endsWith(prefix),
      );
      if (matchedPrefix) {
        supported.add(
          countryName
            .substring(0, countryName.length - matchedPrefix.length)
            .trim(),
        );
      }
    });
    return supported;
  } catch (error) {
    return new Set();
  }
};

export const getExportTabs = ({
  canExpedite,
  country,
  supportedVendorCountries,
}) =>
  TABS.filter((tab) => {
    if (tab === "VENDOR") {
      return !!country && supportedVendorCountries.has(country);
    }
    if (tab === AUTO_PROCESSED_TAB) return canExpedite;
    if (tab === "NOT FOUND") return !canExpedite;
    return true;
  });

// Runs the eConsent results API for an upload until the backend stops asking to continue polling.
export const fetchAllBulkConsentRows = async (uploadId) => {
  const byId = new Map();
  let order = 1;
  let continuePolling = true;

  while (continuePolling) {
    const response = await Api.bulkConsentResult({
      uploadId,
      order,
      last_updated_date: new Date().toISOString(),
    });
    const responseData = response?.data || {};
    getResponseRows(responseData).forEach((row, index) => {
      const mapped = mapBulkConsentRow(row, index);
      byId.set(mapped.id, mapped);
    });

    continuePolling = isTruthy(
      firstValue(
        responseData?.continue_polling,
        responseData?.continuePolling,
        response?.continue_polling,
        response?.continuePolling,
        false,
      ),
    );
    if (continuePolling) {
      order = firstValue(
        responseData?.next_order,
        responseData?.nextOrder,
        response?.next_order,
        response?.nextOrder,
        order,
      );
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }

  return Array.from(byId.values()).sort(
    (a, b) => Number(a.csvRow) - Number(b.csvRow),
  );
};

export const applyAutoProcessedCategory = (rows, canExpedite) =>
  canExpedite
    ? rows.map((row) =>
        row.isAutoProcessedStatus
          ? { ...row, category: AUTO_PROCESSED_TAB }
          : row,
      )
    : rows;

export const exportBulkResultsToExcel = async ({
  mergedRows,
  visibleTabs,
  uploadId,
}) => {
  const extractNameFromFields = (fields = []) => {
    if (!Array.isArray(fields) || fields.length === 0) {
      return "";
    }
    const nameField = fields.find((f) => f.label === "Name");
    if (nameField && nameField.value) {
      return String(nameField.value);
    }
    const firstName = fields.find((f) => f.label === "First Name")?.value || "";
    const lastName = fields.find((f) => f.label === "Last Name")?.value || "";
    return [firstName, lastName].filter(Boolean).join(" ");
  };

  const getDcrStatus = (row) => {
    if (!row?.isDcrApplicable) return "N/A";
    const dcrResult = row?.dcrResult || {};
    if (dcrResult.isMissing) return "N/A";
    if (dcrResult.hasError) return "Failed";
    return "Success";
  };

  const getPhoneConsentStatus = (row) => {
    if (!row?.isConsentApplicable) return "Not Applicable";
    const consentResult = row?.consentResult || {};
    if (consentResult.isMissing || consentResult.phoneConsentStatus == null) {
      return "Pending";
    }
    return consentResult.phoneConsentStatus ? "Success" : "Failed";
  };

  const getEmailConsentStatus = (row) => {
    if (!row?.isConsentApplicable) return "Not Applicable";
    const consentResult = row?.consentResult || {};
    if (consentResult.isMissing || consentResult.emailConsentStatus == null) {
      return "Pending";
    }
    return consentResult.emailConsentStatus ? "Success" : "Failed";
  };

  const headers = [
    "Row",
    "Input",
    "HCP Name",
    "HCO Name",
    "Selected Record (HCP+HCO)",
    "Selected HCP Enterprise ID",
    "Selected HCO Enterprise ID",
    "DCR Status",
    "DCR Details",
    "Subscription Result",
    "Phone Consent Status",
    "Email Consent Status",
    "Consent Result",
  ];

  const toMultiline = (value) => String(value || "").replace(/\s\|\s/g, "\n");
  // normalize vendor display: EPH → Lilly Managed
  const toExportVendor = (v) =>
    String(v || "").toUpperCase() === "EPH" ? "Lilly Managed" : v;

  const getSelectedIds = (row) => {
    const primaryLabel = String(row?.selectionPrimary?.label || "")
      .trim()
      .toUpperCase();
    const parentLabel = String(row?.selectionParent?.label || "")
      .trim()
      .toUpperCase();

    const selectedHcpEnterpriseId =
      primaryLabel === "HCP" ? String(row?.selectionPrimary?.id || "") : "";

    const selectedHcoEnterpriseId = primaryLabel.includes("HCO")
      ? String(row?.selectionPrimary?.id || "")
      : parentLabel.includes("HCO")
        ? String(row?.selectionParent?.id || "")
        : "";

    return { selectedHcpEnterpriseId, selectedHcoEnterpriseId };
  };

  const getDcrResultText = (row) => {
    if (!row?.isDcrApplicable) return "Not Applicable";

    const dcrResult = row?.dcrResult || {};
    const lines = [];
    const addLine = (key, value) => {
      if (value === undefined || value === null || value === "") return;
      lines.push(`${key}: ${String(value)}`);
    };

    if (dcrResult.isMissing) return "No DCR details available";

    if (dcrResult.hasError) {
      lines.push("Status: Failed");
      addLine("Processed", dcrResult.processedStatus);
      addLine("Validation", dcrResult.validationStatus);
      addLine("Error Type", dcrResult.errorType);
      addLine("Error Message", dcrResult.errorMessage);
    } else {
      const dcrIdItems = dcrResult.dcrIdItems || [];
      const resultItems = dcrResult.resultItems || [];

      dcrIdItems.forEach((item, index) => {
        addLine(`DCR Id ${index + 1}`, item?.id);
        addLine(`DCR Id ${index + 1} Vendor`, toExportVendor(item?.vendor));
        if (item?.status !== null && item?.status !== undefined) {
          addLine(
            `DCR Id ${index + 1} Status`,
            item?.status ? "Success" : "Failed",
          );
        }
      });

      resultItems.forEach((item, index) => {
        addLine(`DCR Id ${dcrIdItems.length + index + 1}`, item?.id);
        addLine(
          `DCR Id ${dcrIdItems.length + index + 1} Vendor`,
          toExportVendor(item?.vendor),
        );
      });

      const fallbackItems = (dcrResult.dcrItems || []).filter(
        (item) => item?.sourceType === "fallback",
      );
      fallbackItems.forEach((item, index) => {
        const n = dcrIdItems.length + resultItems.length + index + 1;
        addLine(`DCR Id ${n}`, item?.id);
        addLine(`DCR Id ${n} Vendor`, toExportVendor(item?.vendor));
      });

      if (dcrResult.dcrOutcomeResult) {
        const outcome = dcrResult.dcrOutcomeResult;
        addLine("HCP Enterprise ID", outcome?.hcpEnterpriseId);
        addLine("HCO Enterprise ID", outcome?.hcoEnterpriseId);
        addLine("HCP Entity ID", outcome?.hcpEntityId);
        addLine("HCO Entity ID", outcome?.hcoEntityId);
      }

      if (lines.length === 0) return "No DCR details available";
    }

    return lines.join("\n");
  };

  const getSubscriptionResultText = (row) => {
    if (!row?.isSubscriptionApplicable) return "Not Applicable";

    const subscription = row?.subscriptionResult || {};
    const lines = [];
    const addLine = (key, value) => {
      if (value === undefined || value === null || value === "") return;
      lines.push(`${key}: ${String(value)}`);
    };

    const errors = Array.isArray(subscription.errors) ? subscription.errors : [];
    const shellFailed = subscription.shellProfileStatus === false;

    if (shellFailed || errors.length > 0) {
      lines.push("Status: Failed");
      if (errors.length > 0) {
        errors.forEach((error, index) => addLine(`Error ${index + 1}`, error));
      } else {
        addLine("Error", "Shell profile failed.");
      }
      return lines.join("\n");
    }

    const entityDetails = Array.isArray(subscription.entityDetails)
      ? subscription.entityDetails
      : [];

    entityDetails.forEach((entity, index) => {
      const prefix = entity?.label
        ? String(entity.label)
        : `Entity ${index + 1}`;
      addLine(`${prefix} Source`, mapSourceLabel(entity?.source));
      addLine(`${prefix} Enterprise ID`, entity?.enterpriseId);
      addLine(`${prefix} Entity ID`, entity?.entityId);
      if (entity?.foundInEph !== undefined && entity?.foundInEph !== null) {
        addLine(
          `${prefix} OneKey ID Found In EPH`,
          isTruthy(entity?.foundInEph) ? "Yes" : "No",
        );
      }
    });

    if (lines.length === 0) return "Pending";
    if (subscription.shellProfileStatus === true) {
      lines.unshift("Status: Success");
    }
    return lines.join("\n");
  };

  const getConsentResultText = (row) => {
    if (!row?.isConsentApplicable) return "Not Applicable";

    const consentResult = row?.consentResult || {};
    if (consentResult.isMissing) return "Pending";

    if (
      consentResult.consentUploaded === false ||
      consentResult.emailConsentStatus === false ||
      consentResult.phoneConsentStatus === false ||
      consentResult.emailConsentError ||
      consentResult.phoneConsentError
    ) {
      return "Failed";
    }

    if (
      consentResult.consentUploaded === true ||
      consentResult.emailConsentStatus === true ||
      consentResult.phoneConsentStatus === true
    ) {
      return "Success";
    }

    return "Pending";
  };

  const buildCompleteSelectionData = (selectionObj) => {
    if (!selectionObj) return "";

    const parts = [
      `Label: ${selectionObj.label}`,
      `ID Type: ${selectionObj.idType}`,
      `Source: ${selectionObj.source}`,
      `ID: ${selectionObj.id}`,
    ];

    if (selectionObj.fields && selectionObj.fields.length > 0) {
      selectionObj.fields.forEach((f) => {
        parts.push(`${f.label}: ${f.value}`);
        if (f.details && f.details.length > 0) {
          f.details.forEach((detail) => {
            parts.push(`  ${detail.label}: ${detail.value}`);
          });
        }
      });
    }

    return parts.join("\n");
  };

  const createSheetForTab = (tabName) => {
    const headerRow = headers.map((header) => ({
      value: header,
      fontWeight: "bold",
      textColor: "#FFFFFF",
      fontSize: 11,
      backgroundColor: "#CC1F1F",
      alignVertical: "center",
      align: "left",
      wrap: true,
      height: 24,
      borderColor: "#E5E7EB",
      borderStyle: "thin",
    }));

    const tabRows = mergedRows.filter((r) => r.category === tabName);
    const dataRows = tabRows.map((r, rowIndex) => {
      const inputFields = [
        ...r.inputHcpFields,
        ...r.inputHcoFields,
        ...r.inputParentHcoFields,
      ].filter((field) => field.label !== "Name");
      const inputSummary = [joinFieldValues(inputFields)]
        .filter(Boolean)
        .join(" | ");
      const selectedSummary = [
        buildCompleteSelectionData(r.selectionPrimary),
        buildCompleteSelectionData(r.selectionParent),
      ]
        .filter(Boolean)
        .join("\n\n");

      const { selectedHcpEnterpriseId, selectedHcoEnterpriseId } =
        getSelectedIds(r);

      const hcpName = extractNameFromFields(r.inputHcpFields);
      const hcoName =
        r.inputParentHcoFields.find(
          (field) => field.label.toLowerCase() === "rel hco name",
        )?.value || "";

      const rowValues = [
        r.csvRow,
        toMultiline(inputSummary),
        hcpName || "N/A",
        hcoName || "N/A",
        toMultiline(selectedSummary),
        selectedHcpEnterpriseId || "N/A",
        selectedHcoEnterpriseId || "N/A",
        getDcrStatus(r),
        getDcrResultText(r),
        getSubscriptionResultText(r),
        getPhoneConsentStatus(r),
        getEmailConsentStatus(r),
        getConsentResultText(r),
      ];

      const isEvenRow = (rowIndex + 1) % 2 === 0;

      return rowValues.map((value) => ({
        value: value ?? "",
        alignVertical: "top",
        align: "left",
        wrap: true,
        height: 64,
        borderColor: "#F3F4F6",
        borderStyle: "thin",
        ...(isEvenRow ? { backgroundColor: "#F9FAFB" } : {}),
      }));
    });

    const colWidths = [
      10, 42, 25, 25, 42, 24, 24, 15, 40, 44, 20, 20, 18,
    ].map((width) => ({ width }));

    return {
      sheet: tabName,
      columns: colWidths,
      stickyRowsCount: 1,
      data: [headerRow, ...dataRows],
    };
  };

  const sheets = visibleTabs.map((tab) => createSheetForTab(tab));
  const blob = await writeExcelFile(sheets).toBlob();
  FileSaver.saveAs(blob, `${uploadId}_export.xlsx`);
};
